import { faPlus, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress } from '@material-ui/core';
import { useFormik } from 'formik';
import React, { useMemo } from 'react';
import DomainForm, { DomainFormState, protocolOptions } from '../../../components/muiLegacy/Forms/DomainForm';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';

export interface EditCompetitorsFormProps {
  project: ProjectGq;
  onSuccess: (success: boolean) => Promise<any>;
  handleClose: () => void;
}

const EditCompetitorsForm: React.FC<EditCompetitorsFormProps> = ({
  project,
  onSuccess,
  handleClose,
}) => {

  const existingCompetitors: DomainFormState[] = project.domains
    .filter(d => d.type === DomainTypeGq.COMPETITOR)
    .map(d => ({
      id: d.id,
      domain: d.hostName,
      protocol: !!d.protocol.length ? d.protocol : protocolOptions[0].value,
      type: d.type,
    }));

  const createDomainsMutation = projectsApiService.mutations.domains.useBulkEditCompetitors();
  const formikProps = useFormik<DomainFormState[]>({
    initialValues: [...existingCompetitors, {
      id: null,
      protocol: protocolOptions[0].value,
      domain: '',
      type: DomainTypeGq.COMPETITOR,
    }],
    onSubmit: (values, actions) =>
      createDomainsMutation({
        projectId: project.id,
        domains: values
      })
        .then((result) => onSuccess(result)),
  });

  const competingDomainsMax = 10;
  const addDomainDisabled = useMemo(() => {
    return formikProps.values.length >= competingDomainsMax
  },
    [formikProps.values, project.domains]
  );

  const addDomain = () =>
    formikProps.setValues([
      ...formikProps.values,
      {
        id: null,
        protocol: protocolOptions[0].value,
        domain: '',
        type: DomainTypeGq.COMPETITOR,
      }
    ]);

  return (
    <div className="p-5 w-[600px]">
      <div className="absolute right-2.5 top-2.5">
        <VoxelIconButton
          icon={<FontAwesomeIcon icon={faTimes} size='lg' />}
          color="white"
          onClick={handleClose}
          size='sm'
        />
      </div>
      <VoxelText variant='bread-medium'>{!!existingCompetitors.length ? 'Edit competitors' : 'Add competitors'}</VoxelText>
      <div className=" flex flex-col gap-1">
        <div className="w-full py-4 flex flex-col gap-4">
          <div className="flex flex-col gap-2.5">
            {formikProps.values.map((item, itemIndex) => (
              <div className='flex flex-row items-center gap-0' key={itemIndex}>
                <DomainForm
                  placeholder={'www.organization.com/se-sv'}
                  disabled={formikProps.isSubmitting}
                  size='sm'
                  flexGapClass='gap-2.5'
                  protocolValue={item.protocol}
                  domainValue={item.domain}
                  setProtocolValue={(value) => formikProps.setValues(prev =>
                    prev.map((p, pIndex) => {
                      if (pIndex === itemIndex) {
                        return { ...p, protocol: value }
                      } else { return p }
                    })
                  )}
                  setDomainFormState={(valueFunc) => formikProps.setValues(prev =>
                    prev.map((d, dIndex) => {
                      if (dIndex === itemIndex) {
                        return valueFunc(d)
                      } else { return d }
                    })
                  )}
                  deleteDomainValue={() => formikProps.setValues(prev =>
                    prev.map((d, dIndex) => {
                      if (dIndex === itemIndex) {
                        return { ...d, domain: '' }
                      } else { return d }
                    })
                  )}
                />
                <div className="grow-0">
                  <VoxelIconButton
                    icon={<FontAwesomeIcon icon={faTimes} />}
                    color="white"
                    onClick={() =>
                      formikProps.setValues(formikProps.values.filter((m, i) => i !== itemIndex))
                    }
                  />
                </div>
              </div>
            ))}
            <div className="w-full flex flex-row mt-2 ml-1">
              <VoxelIconButton
                icon={<FontAwesomeIcon icon={faPlus} />}
                size="md"
                color="gray"
                disabled={addDomainDisabled}
                onClick={addDomain}
              />
            </div>
          </div>
          {formikProps.isSubmitting && <LinearProgress />}
        </div>
        <div className="flex flex-row gap-2 justify-end">
          <VoxelButton
            title="Cancel"
            size="sm"
            color="transparent"
            onClick={handleClose}
            disabled={formikProps.isSubmitting}
          />
          <VoxelButton
            title="Save"
            color="success"
            size="sm"
            onClick={formikProps.submitForm}
            disabled={
              formikProps.isSubmitting
              || !formikProps.dirty
            }
          />
        </div>
      </div>
    </div>
  );
};

export default EditCompetitorsForm;
